import { extractCaseIds, parseCaseTokens } from "@/api/club/caseTokens";
import type { ClubComment } from "@/api/club/ClubApi";
import {
  useAddClubComment,
  useCaseChips,
  useEditClubComment,
  useSetClubReaction,
  useWithdrawClubComment,
} from "@/api/club/useClub";
import { useAuth } from "@/auth/AuthContext";
import { formatTimestampDE } from "@/utils/isoDate";
import { useTheme } from "@/theme/ThemeProvider";
import { useStyles } from "@/theme/styles";
import { AppButton } from "@/ui/components/AppButton";
import { CaseChipView } from "@/ui/components/club/CaseChip";
import { ReactionBar } from "@/ui/components/club/ReactionBar";
import { EmptyState } from "@/ui/components/EmptyState";
import { TextField } from "@/ui/components/FormField";
import { SoftSection } from "@/ui/components/SoftSection";
import { LoadingState } from "@/ui/components/StateViews";
import React, { useMemo, useState } from "react";
import { Pressable, Text, View } from "react-native";

const EDIT_WINDOW_MS = 15 * 60 * 1000;

function commentEditOpen(createdAt: string): boolean {
  return Date.now() - Date.parse(createdAt) < EDIT_WINDOW_MS;
}

function CommentBody({
  body,
  withdrawn,
}: {
  body: string | null;
  withdrawn: boolean;
}) {
  const { theme } = useTheme();
  const s = useStyles();
  const caseIds = useMemo(() => extractCaseIds(body), [body]);
  const { byId } = useCaseChips(caseIds, !withdrawn && caseIds.length > 0);

  if (withdrawn) {
    return (
      <Text style={[s.textBody, { color: theme.color.muted, fontStyle: "italic" }]}>
        Kommentar zurückgezogen
      </Text>
    );
  }

  const segments = parseCaseTokens(body ?? "");
  return (
    <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 4, alignItems: "center" }}>
      {segments.map((seg, i) =>
        seg.kind === "text" ? (
          <Text key={i} style={s.textBody}>
            {seg.value}
          </Text>
        ) : (
          <CaseChipView key={i} chip={byId[seg.caseId]} />
        )
      )}
    </View>
  );
}

export function ClubCommentList({
  itemId,
  organizationId,
  comments,
  loading,
  disabled,
}: {
  itemId: string;
  organizationId?: string;
  comments: ClubComment[];
  loading?: boolean;
  disabled?: boolean;
}) {
  const { theme } = useTheme();
  const { user } = useAuth();
  const [draft, setDraft] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editBody, setEditBody] = useState("");

  const add = useAddClubComment(organizationId);
  const edit = useEditClubComment();
  const withdraw = useWithdrawClubComment();
  const react = useSetClubReaction(organizationId);

  const submit = async () => {
    const body = draft.trim();
    if (!body || disabled) return;
    await add.mutateAsync({ itemId, body });
    setDraft("");
  };

  return (
    <SoftSection title="Diskussion">
      <View style={{ gap: theme.space.sm }}>
        {loading ? <LoadingState message="Kommentare laden…" /> : null}

        {!loading && comments.length === 0 ? (
          <EmptyState
            title="Noch keine Kommentare."
            hint="Schreib den ersten Gedanken – die Diskussion startet hier."
          />
        ) : null}

        {comments.map((c) => {
          const isMine = !!user?.id && c.author_id === user.id;
          const canEdit =
            isMine && !c.withdrawn_at && commentEditOpen(c.created_at);
          const editing = editingId === c.id;

          return (
            <View
              key={c.id}
              style={{
                paddingVertical: theme.space.sm,
                paddingHorizontal: theme.space.md,
                borderBottomWidth: 1,
                borderBottomColor: theme.color.border,
                backgroundColor: theme.color.surface,
                gap: theme.space.xs,
              }}
            >
              <Text
                style={{
                  color: theme.color.muted,
                  fontSize: theme.fontSize.tiny,
                  fontWeight: "500",
                }}
              >
                {c.author_display_name ?? "Mitglied"}
                {c.edited_at ? " · bearbeitet" : ""}
                {" · "}
                {formatTimestampDE(c.created_at)}
              </Text>

              {editing ? (
                <View style={{ gap: theme.space.sm }}>
                  <TextField
                    label="Kommentar"
                    value={editBody}
                    onChangeText={setEditBody}
                    multiline
                    numberOfLines={3}
                  />
                  <View style={{ flexDirection: "row", gap: theme.space.sm }}>
                    <AppButton
                      loading={edit.isPending}
                      onPress={async () => {
                        await edit.mutateAsync({
                          commentId: c.id,
                          body: editBody.trim(),
                          itemId,
                        });
                        setEditingId(null);
                      }}
                    >
                      Speichern
                    </AppButton>
                    <AppButton
                      variant="ghost"
                      onPress={() => setEditingId(null)}
                    >
                      Abbrechen
                    </AppButton>
                  </View>
                </View>
              ) : (
                <CommentBody body={c.body} withdrawn={!!c.withdrawn_at} />
              )}

              {!c.withdrawn_at ? (
                <ReactionBar
                  myReaction={c.my_reaction}
                  counts={c.reaction_counts}
                  disabled={disabled || react.isPending}
                  onSelect={(key) =>
                    react.mutate({
                      itemId,
                      commentId: c.id,
                      reactionKey: key,
                    })
                  }
                />
              ) : null}

              {isMine && !c.withdrawn_at && !editing ? (
                <View
                  style={{
                    flexDirection: "row",
                    gap: theme.space.md,
                    paddingTop: 2,
                  }}
                >
                  {canEdit ? (
                    <Pressable
                      onPress={() => {
                        setEditingId(c.id);
                        setEditBody(c.body ?? "");
                      }}
                      hitSlop={8}
                    >
                      <Text
                        style={{
                          color: theme.color.primary,
                          fontSize: theme.fontSize.tiny,
                          fontWeight: "700",
                        }}
                      >
                        Bearbeiten
                      </Text>
                    </Pressable>
                  ) : null}
                  <Pressable
                    onPress={() =>
                      withdraw.mutate({ commentId: c.id, itemId })
                    }
                    hitSlop={8}
                  >
                    <Text
                      style={{
                        color: theme.color.muted,
                        fontSize: theme.fontSize.tiny,
                        fontWeight: "700",
                      }}
                    >
                      Zurückziehen
                    </Text>
                  </Pressable>
                </View>
              ) : null}
            </View>
          );
        })}

        {!disabled ? (
          <View style={{ gap: theme.space.sm, paddingTop: theme.space.sm }}>
            <TextField
              label="Kommentar schreiben"
              value={draft}
              onChangeText={setDraft}
              multiline
              numberOfLines={3}
            />
            <AppButton
              loading={add.isPending}
              disabled={!draft.trim()}
              onPress={() => void submit()}
            >
              Senden
            </AppButton>
          </View>
        ) : null}
      </View>
    </SoftSection>
  );
}
